import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useParams, useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Paper,
  Typography,
  Chip,
  Divider,
  Stack,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Alert,
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CancelIcon from '@mui/icons-material/Cancel';
import CloseIcon from '@mui/icons-material/Close';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DownloadIcon from '@mui/icons-material/Download';
import InsertDriveFileOutlinedIcon from '@mui/icons-material/InsertDriveFileOutlined';
import { AppDispatch, RootState } from '../store/store';
import {
  fetchClaimById,
  getClaimAttachment,
  getInventoryItems,
  downloadInventoryAttachmentFile,
  InventoryAttachment,
  InventoryItem,
} from '../services/claimsApi';
import { config } from '../config';
import { claimColors, claimSx } from '../styles/claimPageStyles';
import { formatDateOnlyDisplay } from '../utils/dateOnly';
import { getStatusChipSx } from '../utils/claimStatusStyles';
import ClaimCommunications from '../components/ClaimCommunications';

const ClaimView: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const dispatch = useDispatch<AppDispatch>();
  const navigate = useNavigate();
  const location = useLocation();
  const [searchParams] = useSearchParams();
  const { currentClaim: claim, loading, error } = useSelector((state: RootState) => state.claims);
  const [inventoryItems, setInventoryItems] = useState<InventoryItem[]>([]);
  const [inventoryPreview, setInventoryPreview] = useState<{ url: string; filename: string } | null>(null);
  const [showUpdateSuccess, setShowUpdateSuccess] = useState(
    () => Boolean((location.state as { claimUpdated?: boolean } | null)?.claimUpdated)
  );

  useEffect(() => {
    if ((location.state as { claimUpdated?: boolean } | null)?.claimUpdated) {
      setShowUpdateSuccess(true);
      navigate(`${location.pathname}${location.search}`, { replace: true, state: null });
    }
  }, [location.pathname, location.search, location.state, navigate]);

  useEffect(() => {
    if (id) {
      dispatch(fetchClaimById(id));
    }
  }, [dispatch, id]);

  useEffect(() => {
    if (!id) return;
    const loadInventoryItems = async () => {
      try {
        const items = await getInventoryItems(id);
        setInventoryItems(Array.isArray(items) ? items : []);
      } catch (inventoryError) {
        console.error('Error loading inventory items:', inventoryError);
        setInventoryItems([]);
      }
    };
    loadInventoryItems();
  }, [id]);

  const claimResumeBaseUrl = useMemo(() => {
    return config.CLAIM_RESUME_BASE_URL.replace(/\/$/, '');
  }, []);

  const claimStatusUrl = useMemo(() => {
    if (!claim?.token) return null;
    return `${claimResumeBaseUrl}/claim-status?claimToken=${encodeURIComponent(claim.token)}`;
  }, [claim?.token, claimResumeBaseUrl]);

  const claimResumeUrl = useMemo(() => {
    if (!claim?.token) return null;
    return `${claimResumeBaseUrl}/?claimToken=${encodeURIComponent(claim.token)}`;
  }, [claim?.token, claimResumeBaseUrl]);

  const copyToClipboard = async (url: string, label: string) => {
    try {
      await navigator.clipboard.writeText(url);
    } catch (copyError) {
      console.error(`Failed to copy ${label}:`, copyError);
    }
  };

  const seeInventoryDamagePlaceholder = '(See Inventory Of Damaged Items below)';

  const isMissingDamageText = (value: string | null | undefined) =>
    value == null || String(value).trim() === '';

  const formatPhoneNumber = (phone: string | null | undefined) => {
    if (!phone) return '';
    const digits = phone.replace(/\D/g, '');
    if (digits.length === 10) {
      return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
    }
    if (digits.length === 11 && digits.startsWith('1')) {
      return `(${digits.slice(1, 4)}) ${digits.slice(4, 7)}-${digits.slice(7)}`;
    }
    return phone;
  };

  const formatDateOnly = (dateString: string | null | undefined) => {
    const formatted = formatDateOnlyDisplay(dateString);
    return formatted || 'Not set';
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    const dateStr = date.toLocaleDateString('en-US', { 
      month: '2-digit', 
      day: '2-digit', 
      year: 'numeric' 
    });
    const timeStr = date.toLocaleTimeString('en-US', { 
      hour: '2-digit', 
      minute: '2-digit',
      hour12: true 
    }).toLowerCase();
    return `${dateStr} ${timeStr}`;
  };

  const formatInventoryMonthYear = (value: string | null | undefined) => {
    if (!value) return 'Not set';
    const [year, month] = value.split('-').map(Number);
    if (!year || !month) return 'Not set';
    return new Date(year, month - 1, 1).toLocaleDateString('en-US', {
      month: 'short',
      year: 'numeric',
    });
  };

  const formatMoney = (value: string | number | null | undefined) => {
    if (value == null || value === '') return '0.00';
    const num = typeof value === 'string' ? Number(value) : value;
    if (Number.isNaN(num)) return '0.00';
    return Number(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const MS_PER_YEAR = 365.25 * 24 * 60 * 60 * 1000;

  const parsePurchaseMonthStartLocal = (yyyyMm: string | null | undefined): Date | null => {
    if (yyyyMm == null || yyyyMm === '') return null;
    const parts = String(yyyyMm).trim().split('-');
    const year = Number(parts[0]);
    const month = Number(parts[1]);
    if (!year || !month || month < 1 || month > 12) return null;
    return new Date(year, month - 1, 1, 12, 0, 0, 0);
  };

  /** Age is measured to incident date when present; otherwise today's date (local noon). */
  const getAsOfDateForInventoryAge = (incidentDate: string | null | undefined): Date => {
    if (incidentDate) {
      const [year, month, day] = String(incidentDate).split('T')[0].split('-').map(Number);
      if (!isNaN(year) && !isNaN(month) && !isNaN(day)) {
        return new Date(year, month - 1, day, 12, 0, 0, 0);
      }
    }
    const d = new Date();
    d.setHours(12, 0, 0, 0);
    return d;
  };

  /** Years from purchase month (first of month) to as-of, rounded to nearest 0.5 (Excel rule). */
  const computeAgeOfItemYearsHalfStep = (
    purchaseYyyyMm: string | null | undefined,
    asOf: Date
  ): string => {
    const purchase = parsePurchaseMonthStartLocal(purchaseYyyyMm);
    if (!purchase) return '';
    let ageYears = (asOf.getTime() - purchase.getTime()) / MS_PER_YEAR;
    if (!Number.isFinite(ageYears) || ageYears < 0) ageYears = 0;
    const rounded = Math.round(ageYears * 2) / 2;
    if (rounded >= 10) return '10 plus';
    if (rounded % 1 === 0) return String(rounded);
    return rounded.toFixed(1);
  };

  const formatCostOfItemUsd = (value: string | number | null | undefined) => `$${formatMoney(value)}`;

  const isImageFilename = (filename: string | undefined) => {
    if (!filename) return false;
    return /\.(png|jpe?g|gif|webp|bmp|svg)$/i.test(filename);
  };

  const isPdfFilename = (filename: string | undefined) => {
    if (!filename) return false;
    return /\.pdf$/i.test(filename);
  };

  const buildInventoryThumbSrc = (item: InventoryItem, attachmentId: string, token?: string) => {
    if (!token) return '';
    return `${config.API_URL}/claims/${item.tpp_claim_id}/inventory-attachments/${attachmentId}/preview?token=${encodeURIComponent(token)}`;
  };

  const renderInventoryAttachmentCell = (claimIdStr: string, item: InventoryItem, file: InventoryAttachment) => {
    const token = file.token;
    const previewUrl = token ? buildInventoryThumbSrc(item, String(file.id), token) : '';
    const image = isImageFilename(file.original_filename);

    const openPreview = () => {
      if (!previewUrl) return;
      setInventoryPreview({ url: previewUrl, filename: file.original_filename });
    };

    const handleDownloadClick = async (e: React.MouseEvent) => {
      e.stopPropagation();
      if (!token) return;
      try {
        await downloadInventoryAttachmentFile(claimIdStr, String(file.id), token, file.original_filename);
      } catch (err) {
        console.error(err);
      }
    };

    return (
      <Box
        key={file.id}
        sx={{
          display: 'inline-flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: 0.25,
          verticalAlign: 'top',
        }}
      >
        <Box
          onClick={openPreview}
          role="button"
          tabIndex={previewUrl ? 0 : -1}
          onKeyDown={(e) => {
            if (!previewUrl) return;
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              openPreview();
            }
          }}
          sx={{
            ...claimSx.thumbBox,
            width: 56,
            height: 56,
            cursor: previewUrl ? 'pointer' : 'default',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          {image && previewUrl ? (
            <Box
              component="img"
              src={previewUrl}
              alt=""
              sx={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
            />
          ) : (
            <InsertDriveFileOutlinedIcon sx={{ color: 'text.disabled', fontSize: 28 }} />
          )}
        </Box>
        {!(image && previewUrl) && file.original_filename && (
          <Typography
            variant="caption"
            title={file.original_filename}
            sx={{
              color: 'text.secondary',
              maxWidth: 160,
              textAlign: 'center',
              lineHeight: 1.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              display: '-webkit-box',
              WebkitLineClamp: 3,
              WebkitBoxOrient: 'vertical',
              wordBreak: 'break-word',
            }}
          >
            {file.original_filename}
          </Typography>
        )}
        <IconButton
          size="small"
          aria-label={`Download ${file.original_filename}`}
          onClick={handleDownloadClick}
          disabled={!token}
          sx={{
            color: claimColors.navy,
            padding: '2px',
            '&:hover': { backgroundColor: 'rgba(0, 30, 96, 0.08)' },
          }}
        >
          <DownloadIcon fontSize="small" />
        </IconButton>
      </Box>
    );
  };

  const { totalPrice } = useMemo(() => {
    const totals = inventoryItems.reduce(
      (acc, item) => {
        const priceNum = Number(item.price ?? 0);
        return {
          totalPrice: acc.totalPrice + (Number.isNaN(priceNum) ? 0 : priceNum),
        };
      },
      { totalPrice: 0 }
    );
    return totals;
  }, [inventoryItems]);

  const renderYesNo = (value: boolean | number) => {
    return value ? (
      <CheckCircleIcon sx={{ color: '#4caf50', fontSize: '1.5rem', verticalAlign: 'middle' }} />
    ) : (
      <CancelIcon sx={{ color: '#f44336', fontSize: '1.5rem', verticalAlign: 'middle' }} />
    );
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'SUBMITTED':
        return 'warning';
      case 'IN_REVIEW':
      case 'PENDING_REVIEW':
        return 'info';
      case 'INCOMPLETE':
        return 'error';
      case 'APPROVED':
        return 'success';
      case 'DENIED':
        return 'error';
      case 'LEGAL_MEDIATION':
      case 'PENDING_SIGNATURE':
        return 'warning';
      case 'PAID':
      case 'SITE_CLOSED':
      case 'CLOSED':
        return 'success';
      case 'DUPLICATE':
        return 'default';
      case 'WITH_OPERATIONS':
        return 'info';
      default:
        return 'default';
    }
  };

  const handleAttachmentDownload = async (claimId: string, attachmentId: string) => {
    try {
      const response = await getClaimAttachment(claimId, attachmentId);
      
      // Get the attachment details
      const attachment = claim?.attachments?.find(a => a.id === attachmentId);
      const filename = attachment?.original_filename || 'attachment';
      
      // Create a temporary link element
      const link = document.createElement('a');
      link.style.display = 'none';
      
      // Create blob URL from the response data
      const url = window.URL.createObjectURL(response.data);
      
      // Set up the link
      link.href = url;
      link.setAttribute('download', filename);
      
      // Add to document and trigger click
      document.body.appendChild(link);
      link.click();
      
      // Clean up
      setTimeout(() => {
        document.body.removeChild(link);
        window.URL.revokeObjectURL(url);
      }, 100);
    } catch (error) {
      console.error('Error downloading attachment:', error);
    }
  };

  if (loading) {
    return (
      <Container>
        <Typography color="text.secondary">Loading...</Typography>
      </Container>
    );
  }

  if (error) {
    return (
      <Container>
        <Typography color="error">{error}</Typography>
      </Container>
    );
  }

  if (!claim) {
    return (
      <Container>
        <Typography color="text.secondary">Claim not found</Typography>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl">
      <Box sx={{ my: 2 }}>
        {showUpdateSuccess && (
          <Alert severity="success" sx={{ mb: 2 }} onClose={() => setShowUpdateSuccess(false)}>
            Claim updated successfully.
          </Alert>
        )}
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', mb: 3, gap: 2 }}>
          <Button
            variant="outlined"
            onClick={() => {
              const queryString = searchParams.toString();
              navigate(`/claims${queryString ? `?${queryString}` : ''}`);
            }}
            sx={claimSx.btnOutlined}
          >
            Back
          </Button>
          <Button
            variant="contained"
            onClick={() => {
              const queryString = searchParams.toString();
              navigate(`/claims/${claim.id}/edit${queryString ? `?${queryString}` : ''}`);
            }}
            sx={claimSx.btnPrimary}
          >
            Edit Claim
          </Button>
        </Box>

        <Paper elevation={0} sx={claimSx.mainPaper}>
          <Stack spacing={4}>
            <Box sx={claimSx.headerBar}>
              <Box sx={{ 
                display: 'flex', 
                flexWrap: 'wrap', 
                justifyContent: 'space-between', 
                alignItems: 'center',
                gap: 2,
              }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 3, alignItems: 'center' }}>
                  <Typography variant="h6" fontWeight="600" sx={{ color: 'text.primary' }}>
                    Claim: <Box component="span" sx={claimSx.claimNumber}>{claim.claim_number}</Box>
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ color: 'text.secondary' }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>Facility:</Box> {claim.facility?.location_code || 'Loading...'}
                  </Typography>
                  <Typography variant="body1" fontWeight="500" sx={{ color: 'text.secondary' }}>
                    <Box component="span" sx={{ fontWeight: 'bold' }}>Unit Number:</Box> {claim.facility_unit}
                  </Typography>
                </Box>
                <Chip
                  label={claim.status}
                  color={getStatusColor(claim.status) as any}
                  sx={getStatusChipSx(claim.status, {
                    fontWeight: '600',
                    fontSize: '0.875rem',
                    height: '32px',
                  })}
                />
              </Box>
              {(claimStatusUrl || claimResumeUrl) && (
                <Stack spacing={1} sx={{ width: '100%' }}>
                  {claimStatusUrl && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        width: '100%',
                        gap: 0.5,
                        fontSize: (theme) => `calc(${theme.typography.h6.fontSize} * 0.5)`,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}
                      >
                        Claim Status Link:
                      </Typography>
                      <Link
                        href={claimStatusUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: claimColors.navy, wordBreak: 'break-all' }}
                      >
                        {claimStatusUrl}
                      </Link>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(claimStatusUrl, 'claim status link')}
                        aria-label="Copy claim status link"
                        sx={{ color: 'text.secondary', p: 0.25 }}
                      >
                        <ContentCopyIcon sx={{ fontSize: '1em' }} />
                      </IconButton>
                    </Box>
                  )}
                  {claimResumeUrl && (
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        flexWrap: 'wrap',
                        width: '100%',
                        gap: 0.5,
                        fontSize: (theme) => `calc(${theme.typography.h6.fontSize} * 0.5)`,
                      }}
                    >
                      <Typography
                        component="span"
                        variant="body2"
                        sx={{ color: 'text.secondary', fontWeight: 600, mr: 0.5 }}
                      >
                        Claim Form Link:
                      </Typography>
                      <Link
                        href={claimResumeUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        sx={{ color: claimColors.navy, wordBreak: 'break-all' }}
                      >
                        {claimResumeUrl}
                      </Link>
                      <IconButton
                        size="small"
                        onClick={() => copyToClipboard(claimResumeUrl, 'claim form link')}
                        aria-label="Copy claim form link"
                        sx={{ color: 'text.secondary', p: 0.25 }}
                      >
                        <ContentCopyIcon sx={{ fontSize: '1em' }} />
                      </IconButton>
                    </Box>
                  )}
                </Stack>
              )}
            </Box>

            <Divider sx={{ my: 1, borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                  Customer Information
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1" sx={{ fontWeight: '600', color: 'text.primary', fontSize: '1.5rem' }}>
                    {claim.reporter_first_name} {claim.reporter_last_name}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {claim.owner_address_1}
                  </Typography>
                  {claim.owner_address_2 && (
                    <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                      {claim.owner_address_2}
                    </Typography>
                  )}
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {claim.owner_city}, {claim.owner_state} {claim.owner_zip_code}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {claim.owner_email}
                  </Typography>
                  <Typography variant="body2" sx={{ color: 'text.secondary', lineHeight: 1.6 }}>
                    {formatPhoneNumber(claim.reporter_phone)}
                  </Typography>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                  Adjuster Information
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Adjuster Loss Type:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.adjuster_loss_type?.name || 'Not set'}</Box>
                  </Typography>
                  {claim.adjuster_loss_type?.name === 'Other' && (
                    <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                      <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Adjusters Loss Type Reason:</Box>{' '}
                      <Box component="span" sx={{ color: 'text.secondary' }}>{claim.other_loss_type || 'Not set'}</Box>
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>TPP Amount:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>${Number(claim.tpp_amount || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>TPP in Place:</Box>
                    {renderYesNo(claim.is_tpp_in_place)}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Account Current:</Box>
                    {renderYesNo(claim.is_account_current)}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Police Report:</Box>
                    {renderYesNo(claim.is_police_report_attached)}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Media Submitted:</Box>
                    {renderYesNo(claim.is_media_submitted)}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Authorities Involved:</Box>
                    {renderYesNo(claim.is_authorities_involved)}
                  </Typography>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                  Disposition
                </Typography>
                <Stack spacing={1.5} sx={{ mb: 3 }}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Paid Amount:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>${Number(claim.total_paid || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Resolved Date:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{formatDateOnly(claim.resolved_date)}</Box>
                  </Typography>
                </Stack>

                <Typography variant="h6" sx={{ ...claimSx.sectionTitle, mt: 3 }}>
                  Internal Information
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Created:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{formatDateTime(claim.created_at)}</Box>
                  </Typography>
                  <Typography variant="body2" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Last Updated:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{formatDateTime(claim.updated_at)}</Box>
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Divider sx={{ my: 1, borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                  Tenant Supplied Claim Information
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Tenant Estimated Value:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      ${Number(claim.damage_estimate ?? 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Incident Date:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{formatDateOnly(claim.incident_date)}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Loss Type:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.loss_type?.name || 'Not set'}</Box>
                  </Typography>
                  {claim.loss_type?.name === 'Other' && claim.other_loss_type_description && (
                    <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                      <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Other Loss Type Description:</Box>{' '}
                      <Box component="span" sx={{ color: 'text.secondary' }}>{claim.other_loss_type_description}</Box>
                    </Typography>
                  )}
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Law Enforcement Name:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.authority_name || 'N/A'}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Law Enforcement Report #:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.authority_filed_report || 'N/A'}</Box>
                  </Typography>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                Tenant Supplied Damage Info
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Store Manager Notified:</Box>
                    {renderYesNo(claim.info_manager_notified)}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Items Current Location:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.items_current_location || 'N/A'}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Damage Discovered By:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.damage_discovered_by || 'N/A'}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Damaged Items:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {isMissingDamageText(claim.damaged_items)
                        ? seeInventoryDamagePlaceholder
                        : claim.damaged_items}
                    </Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, whiteSpace: 'pre-wrap' }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Damage Description:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {isMissingDamageText(claim.damage_description ?? claim.description)
                        ? seeInventoryDamagePlaceholder
                        : (claim.damage_description ?? claim.description)}
                    </Box>
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Divider sx={{ my: 1, borderColor: 'divider' }} />

            <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 4 }}>
              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                  Another Claim Information
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7, display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Other Claims Filed:</Box>
                    {renderYesNo(claim.another_claim_filed)}
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Another Loss Type:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.another_loss_type?.name || 'Not set'}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Another Claim Date:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>
                      {claim.another_claim_date ? formatDateOnly(claim.another_claim_date) : 'N/A'}
                    </Box>
                  </Typography>
                </Stack>
              </Paper>

              <Paper elevation={0} sx={claimSx.sectionCard}>
                <Typography variant="h6" sx={claimSx.sectionTitle}>
                  Adjuster Notes
                </Typography>
                <Stack spacing={1.5}>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Progress Notes:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.note || 'N/A'}</Box>
                  </Typography>
                  <Typography variant="body1" sx={{ lineHeight: 1.7 }}>
                    <Box component="span" sx={{ fontWeight: '600', color: 'text.primary' }}>Deny Reason:</Box>{' '}
                    <Box component="span" sx={{ color: 'text.secondary' }}>{claim.deny_reason || 'N/A'}</Box>
                  </Typography>
                </Stack>
              </Paper>
            </Box>

            <Stack spacing={3}>
              <Paper elevation={0} sx={{ ...claimSx.sectionCard, p: 3 }}>
                <Box sx={claimSx.sectionTitleRow}>
                  <Typography variant="h6" sx={{ color: claimColors.navy, fontWeight: 600 }}>
                    Claim Attachments
                  </Typography>
                  <Button
                    variant="outlined"
                    size="small"
                    onClick={() => {
                      const queryString = searchParams.toString();
                      navigate(`/claims/${id}/edit/attachments${queryString ? `?${queryString}` : ''}`);
                    }}
                    sx={claimSx.btnOutlinedAccent}
                  >
                    Edit Attachments
                  </Button>
                </Box>
                <Stack spacing={2}>
                  {claim.attachments && claim.attachments.length > 0 ? (
                    claim.attachments.map((attachment, index) => (
                      <Box
                        key={attachment.id}
                        sx={claimSx.nestedRow}
                      >
                        <Typography variant="body1" sx={{ fontWeight: '600', color: 'text.primary', minWidth: '120px' }}>
                          Attachment {index + 1}:
                        </Typography>
                        <Chip
                          label={attachment.type}
                          size="small"
                          sx={claimSx.attachmentChip}
                        />
                        <Link
                          component="button"
                          onClick={() => handleAttachmentDownload(claim.id, attachment.id)}
                          sx={claimSx.link}
                        >
                          {attachment.original_filename}
                        </Link>
                        <Box sx={{ flexGrow: 1 }} />
                        {!attachment.user_id && (
                          <Typography variant="caption" sx={{ color: 'text.disabled', fontStyle: 'italic' }}>
                            (uploaded by tenant)
                          </Typography>
                        )}
                      </Box>
                    ))
                  ) : (
                    <Typography variant="body1" sx={claimSx.emptyText}>
                      No attachments found
                    </Typography>
                  )}
                </Stack>
              </Paper>

              <Paper elevation={0} sx={{ ...claimSx.sectionCard, p: 3 }}>
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'space-between', pb: 1, mb: 2, borderBottom: `2px solid ${claimColors.red}` }}>
                  <Typography variant="h6" sx={{ color: claimColors.navy, fontWeight: '600' }}>
                    Inventory Of Damaged Items
                  </Typography>
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, alignItems: 'center', justifyContent: 'flex-end' }}>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      <Box component="span" sx={{ color: 'text.secondary' }}>Num Items:</Box> {inventoryItems.length}
                    </Typography>
                    <Typography variant="body2" sx={{ color: 'text.primary' }}>
                      <Box component="span" sx={{ color: 'text.secondary' }}>Total Price:</Box> ${formatMoney(totalPrice)}
                    </Typography>
                    {inventoryItems.length > 0 && (
                      <Button
                        variant="outlined"
                        size="small"
                        onClick={() => {
                          const asOfInventoryAge = getAsOfDateForInventoryAge(claim.incident_date);
                          const ageHeader = 'Age of Item Years (.5 Year Rounding)';
                          const csvRows = [
                            ['Item Description', ageHeader, 'Cost of Item', 'Purchase Date'],
                            ...inventoryItems.map((item) => [
                              String(item.item_description ?? ''),
                              computeAgeOfItemYearsHalfStep(item.date_of_purchase, asOfInventoryAge),
                              formatCostOfItemUsd(item.price),
                              formatInventoryMonthYear(item.date_of_purchase),
                            ]),
                          ];
                          const csv = csvRows
                            .map((row) => row.map((cell) => `"${String(cell).replace(/"/g, '""')}"`).join(','))
                            .join('\n');
                          const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
                          const url = URL.createObjectURL(blob);
                          const link = document.createElement('a');
                          link.href = url;
                          link.download = `${claim.claim_number || 'claim'}-inventory.csv`;
                          document.body.appendChild(link);
                          link.click();
                          document.body.removeChild(link);
                          URL.revokeObjectURL(url);
                        }}
                        sx={{ ...claimSx.btnOutlinedAccent, borderRadius: 10, minWidth: 64 }}
                      >
                        Export
                      </Button>
                    )}
                  </Box>
                </Box>

                {inventoryItems.length === 0 ? (
                  <Typography variant="body1" sx={claimSx.emptyText}>
                    No inventory items found.
                  </Typography>
                ) : (
                  <Stack spacing={2}>
                    {inventoryItems.map((item) => {
                      const attachments = Array.isArray(item.attachments) ? item.attachments : [];
                      const receiptFiles = attachments.filter((a) => a.type === 'inventory_receipt');
                      const evidenceFiles = attachments.filter((a) => a.type === 'inventory_evidence');

                      return (
                        <Box key={item.id} sx={claimSx.inventoryRow}>
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap' }}>
                            <Box sx={{ flex: '1 1 320px', minWidth: 0 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary', mb: 1 }}>
                                {item.item_description}
                              </Typography>
                            <Typography
                              variant="body2"
                              sx={{
                                color: 'text.secondary',
                                mb: 1,
                                whiteSpace: 'pre-wrap',
                                fontStyle: 'italic',
                              }}
                            >
                              Damage Description: {item.damage_description || 'Not provided'}
                            </Typography>
                            </Box>
                            <Box sx={{ textAlign: { xs: 'left', sm: 'right' }, minWidth: 170 }}>
                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                Cost of Item: {formatCostOfItemUsd(item.price)}
                              </Typography>
                              <Typography variant="body2" sx={{ color: 'text.primary' }}>
                                Purchase Date: {formatInventoryMonthYear(item.date_of_purchase)}
                              </Typography>
                            </Box>
                          </Box>

                          <Box sx={{ display: 'flex', gap: 4, mt: 1.5, flexWrap: 'wrap' }}>
                            <Box sx={{ minWidth: 180 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Evidence:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                {evidenceFiles.length ? (
                                  evidenceFiles.map((file) => renderInventoryAttachmentCell(String(claim.id), item, file))
                                ) : (
                                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>None</Typography>
                                )}
                              </Box>
                            </Box>
                            <Box sx={{ minWidth: 180 }}>
                              <Typography variant="caption" sx={{ color: 'text.secondary', display: 'block', mb: 0.5 }}>
                                Receipt:
                              </Typography>
                              <Box sx={{ display: 'flex', gap: 1.5, flexWrap: 'wrap', alignItems: 'flex-start' }}>
                                {receiptFiles.length ? (
                                  receiptFiles.map((file) => renderInventoryAttachmentCell(String(claim.id), item, file))
                                ) : (
                                  <Typography variant="caption" sx={{ color: 'text.disabled' }}>None</Typography>
                                )}
                              </Box>
                            </Box>
                          </Box>
                        </Box>
                      );
                    })}
                  </Stack>
                )}
              </Paper>
            </Stack>

            {id && (
              <>
                <Divider sx={{ my: 1, borderColor: 'divider' }} />
                <ClaimCommunications claimId={id} />
              </>
            )}
          </Stack>
        </Paper>
      </Box>

      <Dialog
        open={!!inventoryPreview}
        onClose={() => setInventoryPreview(null)}
        maxWidth="md"
        fullWidth
        PaperProps={{ sx: { backgroundColor: 'background.paper', color: 'text.primary' } }}
      >
        <DialogTitle
          sx={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
            pr: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
          }}
        >
          <Typography
            component="span"
            variant="subtitle1"
            sx={{ overflow: 'hidden', textOverflow: 'ellipsis', fontWeight: 600 }}
            title={inventoryPreview?.filename}
          >
            {inventoryPreview?.filename}
          </Typography>
          <IconButton aria-label="Close preview" onClick={() => setInventoryPreview(null)} sx={{ color: 'text.primary' }}>
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent sx={{ borderColor: 'divider', pt: 2, minHeight: 200 }}>
          {inventoryPreview &&
            (isImageFilename(inventoryPreview.filename) ? (
              <Box
                component="img"
                src={inventoryPreview.url}
                alt=""
                sx={{
                  maxWidth: '100%',
                  maxHeight: '70vh',
                  objectFit: 'contain',
                  display: 'block',
                  mx: 'auto',
                }}
              />
            ) : isPdfFilename(inventoryPreview.filename) ? (
              <Box
                component="iframe"
                src={inventoryPreview.url}
                title={inventoryPreview.filename}
                sx={{ width: '100%', height: '70vh', border: 'none', borderRadius: 1, backgroundColor: 'background.default' }}
              />
            ) : (
              <Typography sx={{ color: 'text.secondary', py: 2 }}>
                Inline preview is not available for this file type. Use the download icon under the thumbnail to save a
                copy.
              </Typography>
            ))}
        </DialogContent>
      </Dialog>
    </Container>
  );
};

export default ClaimView; 